import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '@h2/ui';
import Icon from '../../components/ui/icon';
import { useJobStore, Job, Task, LanguageCode } from '../../stores/useJobStore';

interface JobSettingsWizardPageProps {}

const JobSettingsWizardPage: React.FC<JobSettingsWizardPageProps> = () => {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const { jobs, workers, updateJob } = useJobStore();
  
  const [job, setJob] = useState<Job | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    sourceLanguage: 'KO' as LanguageCode,
    targetLanguages: [] as LanguageCode[],
    deadline: '',
    priority: 'medium' as Job['priority'],
    tasks: [] as Task[]
  });

  // Job 데이터 로드
  useEffect(() => {
    if (jobId) {
      const foundJob = jobs.find(j => j.id === jobId);
      if (foundJob) {
        setJob(foundJob);
        setFormData({
          title: foundJob.title,
          description: foundJob.description || '',
          sourceLanguage: foundJob.sourceLanguage,
          targetLanguages: [...foundJob.targetLanguages],
          deadline: foundJob.deadline,
          priority: foundJob.priority,
          tasks: [...foundJob.tasks]
        });
      }
    }
  }, [jobId, jobs]);

  const steps = [
    { id: 1, title: '기본 정보', icon: 'FileText' },
    { id: 2, title: '언어 설정', icon: 'Globe' },
    { id: 3, title: '작업 플로우', icon: 'GitBranch' },
    { id: 4, title: '작업자 배정', icon: 'Users' },
    { id: 5, title: '최종 확인', icon: 'Check' }
  ];

  const languageOptions = [
    { value: 'KO', label: '한국어', flag: '🇰🇷' },
    { value: 'EN', label: '영어', flag: '🇺🇸' },
    { value: 'JA', label: '일본어', flag: '🇯🇵' },
    { value: 'ZH', label: '중국어', flag: '🇨🇳' },
    { value: 'ES', label: '스페인어', flag: '🇪🇸' },
    { value: 'FR', label: '프랑스어', flag: '🇫🇷' },
    { value: 'DE', label: '독일어', flag: '🇩🇪' }
  ];

  const taskTypeOptions = [
    { value: '전사', label: '전사', description: '음성을 텍스트로 변환' },
    { value: '전사검수', label: '전사검수', description: '전사 내용의 정확성 검토' },
    { value: '번역', label: '번역', description: '다른 언어로 번역' },
    { value: '번역검수', label: '번역검수', description: '번역 품질 검토' }
  ];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = () => {
    if (job) {
      updateJob(job.id, {
        title: formData.title,
        description: formData.description,
        sourceLanguage: formData.sourceLanguage,
        targetLanguages: formData.targetLanguages,
        deadline: formData.deadline,
        priority: formData.priority,
        tasks: formData.tasks
      });
    }
    navigate('/work/manage');
  };

  const handleCancel = () => {
    navigate('/work/manage');
  };

  // Step 1: 기본 정보
  const renderBasicInfo = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        프로젝트 기본 정보
      </h3>
      
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            프로젝트 제목
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700"
            placeholder="프로젝트 제목을 입력하세요"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            프로젝트 설명
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700"
            placeholder="프로젝트에 대한 상세 설명을 입력하세요"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              마감일
            </label>
            <input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              우선순위
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as Job['priority'] })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700"
            >
              <option value="low">낮음</option>
              <option value="medium">보통</option>
              <option value="high">높음</option>
              <option value="urgent">긴급</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // Step 2: 언어 설정
  const renderLanguageSettings = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        언어 설정
      </h3>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            원본 언어
          </label>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {languageOptions.map(lang => (
              <button
                key={lang.value}
                onClick={() => setFormData({ ...formData, sourceLanguage: lang.value as LanguageCode })}
                className={`
                  flex items-center gap-2 p-3 border rounded-lg text-sm font-medium transition-colors
                  ${formData.sourceLanguage === lang.value
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }
                `}
              >
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            대상 언어 (다중 선택 가능)
          </label>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {languageOptions.filter(lang => lang.value !== formData.sourceLanguage).map(lang => (
              <button
                key={lang.value}
                onClick={() => {
                  const isSelected = formData.targetLanguages.includes(lang.value as LanguageCode);
                  if (isSelected) {
                    setFormData({
                      ...formData,
                      targetLanguages: formData.targetLanguages.filter(l => l !== lang.value)
                    });
                  } else {
                    setFormData({
                      ...formData,
                      targetLanguages: [...formData.targetLanguages, lang.value as LanguageCode]
                    });
                  }
                }}
                className={`
                  flex items-center gap-2 p-3 border rounded-lg text-sm font-medium transition-colors
                  ${formData.targetLanguages.includes(lang.value as LanguageCode)
                    ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300'
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }
                `}
              >
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Step 3: 작업 플로우
  const renderWorkflow = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        작업 플로우 설정
      </h3>

      <div className="space-y-4">
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-yellow-600 dark:text-yellow-400 mt-0.5" />
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium mb-1">권장 작업 플로우</p>
              <p>1. 전사 → 2. 전사검수 → 3. 번역 (각 대상 언어별) → 4. 번역검수 (각 대상 언어별)</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {taskTypeOptions.map(taskType => (
            <div
              key={taskType.value}
              className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">
                  {taskType.label}
                </h4>
                <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200">
                  <Icon name="Settings" size={16} />
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {taskType.description}
              </p>
              
              {taskType.value === '번역' || taskType.value === '번역검수' ? (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  대상 언어: {formData.targetLanguages.join(', ')} ({formData.targetLanguages.length}개 작업)
                </div>
              ) : (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  원본 언어: {formData.sourceLanguage} (1개 작업)
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Step 4: 작업자 배정
  const renderWorkerAssignment = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        작업자 배정
      </h3>

      <div className="space-y-4">
        {workers.map(worker => (
          <div
            key={worker.id}
            className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    {worker.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {worker.name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {worker.level} • {worker.specialties.join(', ')}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">
                    언어: {worker.languages.map(lang => {
                      const langOption = languageOptions.find(l => l.value === lang);
                      return langOption ? `${langOption.flag} ${lang}` : lang;
                    }).join(' ')}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <select className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700">
                  <option value="">배정 없음</option>
                  <option value="전사">전사</option>
                  <option value="전사검수">전사검수</option>
                  <option value="번역">번역</option>
                  <option value="번역검수">번역검수</option>
                </select>
                <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <Icon name="MoreVertical" size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Step 5: 최종 확인
  const renderConfirmation = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        최종 확인
      </h3>

      <div className="space-y-4">
        <Card>
          <div className="p-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">프로젝트 정보</h4>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-500">제목:</span> {formData.title}</div>
              <div><span className="text-gray-500">설명:</span> {formData.description}</div>
              <div><span className="text-gray-500">마감일:</span> {formData.deadline}</div>
              <div><span className="text-gray-500">우선순위:</span> {formData.priority}</div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">언어 설정</h4>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-500">원본 언어:</span> {formData.sourceLanguage}</div>
              <div><span className="text-gray-500">대상 언어:</span> {formData.targetLanguages.join(', ')}</div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">예상 작업 수</h4>
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <div>• 전사: 1개</div>
              <div>• 전사검수: 1개</div>
              <div>• 번역: {formData.targetLanguages.length}개</div>
              <div>• 번역검수: {formData.targetLanguages.length}개</div>
              <div className="pt-2 border-t border-gray-200 dark:border-gray-600 font-medium text-gray-900 dark:text-gray-100">
                총 {2 + (formData.targetLanguages.length * 2)}개 작업
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderBasicInfo();
      case 2: return renderLanguageSettings();
      case 3: return renderWorkflow();
      case 4: return renderWorkerAssignment();
      case 5: return renderConfirmation();
      default: return renderBasicInfo();
    }
  };

  if (!job) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500 dark:text-gray-400">작업을 찾을 수 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto p-6">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <Icon name="ArrowLeft" size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                작업 설정 {job ? '수정' : '생성'}
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {job?.title || '새 프로젝트'}
              </p>
            </div>
          </div>

          {/* 진행 단계 */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors
                      ${currentStep >= step.id
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                      }
                    `}
                  >
                    {currentStep > step.id ? (
                      <Icon name="Check" size={16} />
                    ) : (
                      <Icon name={step.icon as any} size={16} />
                    )}
                  </div>
                  <span className="mt-2 text-xs text-gray-600 dark:text-gray-400 text-center">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 컨텐츠 */}
        <Card>
          <div className="p-6">
            {renderStepContent()}
          </div>
        </Card>

        {/* 하단 버튼 */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="ChevronLeft" size={16} />
            이전
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              취소
            </button>
            
            {currentStep === steps.length ? (
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Icon name="Save" size={16} />
                저장
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                다음
                <Icon name="ChevronRight" size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSettingsWizardPage;